import { useEffect, useRef, useState } from "react";
import { useHistory } from "react-router-dom";
import { constants, logEducatorDashboard, LogLevel } from "utils";
import { request } from "hooks/useOkoApi/useOkoApi";
import { trackClickedButton } from "utils/analytics";
import { createQuestionSet, getQuestionSet, getStandardLearningComponents } from "hooks/useCurriculum/useCurriculum";

// Everything a quick group card does, whatever it looks like: the saved set's
// size, the LC names, copy, the session length save, duplicate, release and
// the edit navigation. Shared by today's QuickGroupCard and the one-list card.
//
// The optional `on…` callbacks report outcomes (a duplicate the server
// created, a release that went through, a saved length) to a caller that
// wants them; today's card passes none.

const COPIED_FEEDBACK_MS = 2000;
const DEFAULT_QUICK_SESSION_LENGTH = 15;

const wholeMinutes = (value) => (Number.isFinite(value) ? Math.round(value) : DEFAULT_QUICK_SESSION_LENGTH);

// The card payload sizes the standard's filtered bank, not the saved set, so the
// set's own size is read from the set. One request per card that has one; dropped
// once question_count reflects the set.
const useQuestionSetSize = (questionSetId) => {
  const [setSize, setSetSize] = useState(null);
  useEffect(() => {
    if (!questionSetId) { setSetSize(null); return undefined; }
    let cancelled = false;
    (async () => {
      try {
        const set = await getQuestionSet(questionSetId);
        const size = (set?.questions || set?.question_set_questions || []).length;
        if (!cancelled) setSetSize(Number.isFinite(size) ? size : null);
      } catch {
        /* leave unknown; the label renders without a count */
      }
    })();
    return () => { cancelled = true; };
  }, [questionSetId]);
  return setSize;
};

// The group row stores only the filter's LC ids; bundle cards get names resolved
// server-side on the assignment, so this card resolves them itself to render the
// same tag / title / hover the standard cards do.
const useLearningComponentNames = (standardCode, questionFilter) => {
  const lcIds = questionFilter?.learning_components || [];
  const lcKey = lcIds.join("|");
  const [learningComponents, setLearningComponents] = useState([]);
  useEffect(() => {
    const ids = lcKey ? lcKey.split("|") : [];
    if (!ids.length || !standardCode) { setLearningComponents([]); return undefined; }
    let cancelled = false;
    (async () => {
      // Stored order is the teacher's and drives the LC1/LC2 numbering, so the
      // selection is mapped in place rather than taking the response's order.
      let nameById = new Map();
      try {
        const data = await getStandardLearningComponents(standardCode);
        nameById = new Map((data?.learning_components || []).map((lc) => [lc.lc_id, lc.description]));
      } catch {
        /* unresolved names fall back to the id, which lcName already handles */
      }
      if (!cancelled) setLearningComponents(ids.map((id) => ({ lc_id: id, lc_name: nameById.get(id) })));
    })();
    return () => { cancelled = true; };
  }, [standardCode, lcKey]);
  return learningComponents;
};

export const useQuickGroupActions = ({
  quickGroup,
  classroomId,
  onDeleted,
  onDuplicated,
  onDuplicateSucceeded,
  onReleased,
  onSessionSaved,
}) => {
  const {
    id,
    code,
    standard_code: standardCode,
    standard_name: standardName,
    session_length: sessionLength,
    question_filter: questionFilter,
    question_set_id: questionSetId,
  } = quickGroup;
  const history = useHistory();
  const setSize = useQuestionSetSize(questionSetId);
  const learningComponents = useLearningComponentNames(standardCode, questionFilter);
  const [copied, setCopied] = useState(false);
  const [sessionValue, setSessionValue] = useState(wholeMinutes(sessionLength));
  // The dropdown works in whole minutes, so a legacy row at 17.5 shows as 18. Keep
  // the untouched original so a duplicate copies the stored value rather than the
  // rounded display of it.
  const initialSessionRef = useRef(null);
  if (initialSessionRef.current === null) initialSessionRef.current = wholeMinutes(sessionLength);
  const [savingSession, setSavingSession] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [duplicating, setDuplicating] = useState(false);
  const [duplicateError, setDuplicateError] = useState(null);
  const duplicatingRef = useRef(false);
  // Synchronous re-entrancy guard: the `deleting` state only disables the
  // action on the next render, so a same-tick double-dispatch could fire a
  // second DELETE on the just-removed group (→ 404). The ref blocks that.
  const releasingRef = useRef(false);

  // Edit reuses the Create Quick Group flow (same full-page standard picker) in
  // edit mode: the editCode/editGroupId params switch it to "Group X: Edit
  // Content" + a Save CTA that updates this group instead of creating one.
  const handleEdit = () => {
    logEducatorDashboard("Educator dashboard: Quick group edit clicked", {
      event: "quick_group_edit_clicked",
      groupCode: code,
    });
    const params = new URLSearchParams({
      classroomId: classroomId || "",
      classId: classroomId || "",
      editCode: (code || "").toUpperCase(),
      editGroupId: id || "",
      standard: standardCode || "",
      standardName: standardName || "",
    });
    history.push(`${constants.CREATE_QUICK_GROUP_PATH}?${params}`);
  };

  // Archive/release the quick group: removes the card and frees the group code
  // for reuse (Figma 4253-25373 confirmation). Mirrors the lesson Archive CTA:
  // in-flight guard against a duplicate DELETE, 404 treated as the expected
  // end state, and the list re-fetched in `finally` regardless of outcome.
  const handleRelease = async () => {
    if (!classroomId || !id || releasingRef.current) return;
    releasingRef.current = true;
    setDeleting(true);
    // Close the modal on confirm; the list re-fetches in `finally`.
    setConfirmOpen(false);
    try {
      await request(`quick-groups/classrooms/${classroomId}/${id}`, { method: "DELETE" });
      logEducatorDashboard("Educator dashboard: Quick group released", {
        event: "quick_group_released",
        groupCode: code,
        quickGroupId: id,
      });
      onReleased?.();
    } catch (e) {
      // A 404 means the group is already released — the expected end state, so
      // we don't treat it as an error. Other failures are logged (not surfaced).
      if (e?.status === 404) {
        onReleased?.();
      } else {
        logEducatorDashboard("Educator dashboard: Failed to release quick group", {
          level: LogLevel.WARNING,
          event: "quick_group_release_error",
          groupCode: code,
          quickGroupId: id,
          statusCode: e?.status,
          errorMessage: e?.detail || e?.message || "An unknown error occurred.",
        });
      }
    } finally {
      await onDeleted?.();
      releasingRef.current = false;
      setDeleting(false);
    }
  };

  // Duplicate = claim another free code for the same standard. The lesson cards
  // stage a duplicate as a draft row and commit it on Save; a quick group has no
  // draft mode, so this creates the copy immediately (same as Create Quick Group).
  const handleDuplicate = async () => {
    if (!classroomId || !standardCode || duplicatingRef.current) return;
    duplicatingRef.current = true;
    setDuplicating(true);
    setDuplicateError(null);
    let created = null;
    try {
      created = await request("quick-groups", {
        method: "POST",
        payload: {
          classroom_id: classroomId,
          standard_code: standardCode,
          // The live value, not the row's — a length the teacher just changed
          // should carry to the copy. Untouched, the stored value wins over its
          // rounded display so 17.5 duplicates as 17.5, not 18.
          session_length: sessionValue === initialSessionRef.current && Number.isFinite(sessionLength)
            ? sessionLength
            : sessionValue,
          ...(questionFilter ? { question_filter: questionFilter } : {}),
        },
      });
      onDuplicateSucceeded?.(created);
      // A saved set can't be pointed at a second quick group — the apply endpoint
      // resolves bundle groups only — so the copy gets its own set with the same
      // questions. Sets are immutable, so a shared and a copied set behave alike.
      if (questionSetId && created?.code) {
        const source = await getQuestionSet(questionSetId);
        const questions = (source?.questions || []).map((q) => ({
          challenge_id: q.challenge_id,
          challenge_type: q.challenge_type,
        }));
        if (questions.length > 0) {
          await createQuestionSet(classroomId, {
            group_code: created.code,
            questions,
            // Carried, not defaulted: creating a set rewrites the group's stored
            // question_filter from these two, so omitting them would wipe the
            // filter the POST above just set.
            filter_state: source?.filter_state ?? null,
            auto_assign_early_finishers: !!source?.auto_assign_early_finishers,
          });
        }
      }
      logEducatorDashboard("Educator dashboard: Quick group duplicated", {
        event: "quick_group_duplicated",
        groupCode: code,
        newGroupCode: created?.code || null,
        quickGroupId: id,
      });
    } catch (e) {
      // A failure after the group exists is half-done, so it says so rather than
      // reading as "nothing happened" — the copy is there, without its set.
      setDuplicateError(
        created
          ? "Group duplicated, but its question set didn't copy over. Open the new group to rebuild it."
          : e?.status === 409
            ? "All group codes in this class are in use. Free one up by deleting a quick group or unassigning a code."
            : e?.detail || e?.message || "Failed to duplicate this group. Please try again."
      );
      logEducatorDashboard("Educator dashboard: Failed to duplicate quick group", {
        level: LogLevel.WARNING,
        event: "quick_group_duplicate_error",
        groupCode: code,
        quickGroupId: id,
        statusCode: e?.status,
        errorMessage: e?.detail || e?.message || "An unknown error occurred.",
      });
    } finally {
      // Refetch either way: on a partial failure the new group still exists.
      await onDuplicated?.();
      duplicatingRef.current = false;
      setDuplicating(false);
    }
  };

  // A quick group's code is a real group code, so its session length lives in
  // the same group config the lesson cards edit. Fetch-then-merge: read the
  // server's current config and change only session_length, so we never clobber
  // activity_type / standard_prefix with a guessed value (POST group/{code} —
  // the request helper defaults to POST, matching GroupCodeCard).
  const handleSessionChange = async (next) => {
    if (!Number.isFinite(next) || next === sessionValue) return;
    const previous = sessionValue;
    setSessionValue(next);
    setSavingSession(true);
    try {
      const current = await request(`group/${code}`, { method: "GET" });
      await request(`group/${code}`, {
        payload: { config: { ...(current?.config || {}), session_length: next } },
      });
      logEducatorDashboard("Educator dashboard: Quick group session length changed", {
        event: "quick_group_session_length_changed",
        groupCode: code,
        previousLength: previous,
        newLength: next,
      });
      onSessionSaved?.(next);
    } catch (e) {
      setSessionValue(previous);
      logEducatorDashboard("Educator dashboard: Failed to update quick group session length", {
        level: LogLevel.ERROR,
        event: "quick_group_session_length_error",
        groupCode: code,
        statusCode: e?.status,
        errorMessage: e?.detail || e?.message || "An unknown error occurred.",
      });
    } finally {
      setSavingSession(false);
    }
  };

  const copyShareLink = () => {
    const upperCode = (code || "").toUpperCase();
    trackClickedButton("Copy Code", { page_name: "Dashboard", group_code: upperCode, card_type: "Quick Group" });
    navigator.clipboard?.writeText(`${process.env.REACT_APP_OKOLABS_APP_URL}?code=${upperCode}`);
    logEducatorDashboard("Educator dashboard: Quick group code link copied", {
      event: "quick_group_code_copied",
      groupCode: code,
    });
    setCopied(true);
    setTimeout(() => setCopied(false), COPIED_FEEDBACK_MS);
  };

  return {
    setSize,
    learningComponents,
    copied,
    copyShareLink,
    sessionValue,
    savingSession,
    handleSessionChange,
    duplicating,
    duplicateError,
    handleDuplicate,
    confirmOpen,
    setConfirmOpen,
    deleting,
    handleRelease,
    handleEdit,
  };
};
